import { useEffect, useState } from 'react'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import { useTranslation } from '../contexts/TranslationContext'

export default function LegalDocumentEditor({ value, readOnly, onSave, onCancel, saving }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    version: '',
    locale: 'en',
    title: '',
    contentMarkdown: '',
    effectiveFrom: '',
    changeSummary: '',
    requiresReacceptance: false,
  })
  const [error, setError] = useState('')

  useEffect(() => {
    if (!value) return
    setForm({
      version: value.version || '',
      locale: value.locale || 'en',
      title: value.title || '',
      contentMarkdown: value.contentMarkdown || value.content_markdown || '',
      effectiveFrom: value.effectiveFrom ? String(value.effectiveFrom).slice(0, 10) : '',
      changeSummary: value.changeSummary || value.change_summary || '',
      requiresReacceptance: Boolean(value.requiresReacceptance || value.requires_reacceptance),
    })
  }, [value])

  const submit = async () => {
    if (!form.version.trim() || !form.title.trim() || !form.contentMarkdown.trim()) {
      setError(t('Version, title and content are required.', 'Version, title and content are required.'))
      return
    }
    setError('')
    await onSave({
      version: form.version.trim(),
      locale: form.locale.trim() || 'en',
      title: form.title.trim(),
      contentMarkdown: form.contentMarkdown.trim(),
      effectiveFrom: form.effectiveFrom || null,
      changeSummary: form.changeSummary.trim() || null,
      requiresReacceptance: form.requiresReacceptance,
    })
  }

  return (
    <div className="space-y-3 rounded-md border border-outline p-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input
          placeholder={t('Version', 'Version')}
          value={form.version}
          onChange={(e) => setForm((prev) => ({ ...prev, version: e.target.value }))}
          disabled={readOnly}
        />
        <Input
          placeholder={t('Locale', 'Locale')}
          value={form.locale}
          onChange={(e) => setForm((prev) => ({ ...prev, locale: e.target.value }))}
          disabled={readOnly}
        />
      </div>
      <Input
        placeholder={t('Title', 'Title')}
        value={form.title}
        onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
        disabled={readOnly}
      />
      <Input
        type="date"
        value={form.effectiveFrom}
        onChange={(e) => setForm((prev) => ({ ...prev, effectiveFrom: e.target.value }))}
        disabled={readOnly}
      />
      <Input
        placeholder={t('Change summary', 'Change summary')}
        value={form.changeSummary}
        onChange={(e) => setForm((prev) => ({ ...prev, changeSummary: e.target.value }))}
        disabled={readOnly}
      />
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={form.requiresReacceptance}
          onChange={(e) => setForm((prev) => ({ ...prev, requiresReacceptance: e.target.checked }))}
          disabled={readOnly}
        />
        {t('Requires reacceptance', 'Requires reacceptance')}
      </label>
      <textarea
        className="min-h-[160px] w-full rounded-md border border-outline p-2 text-sm"
        value={form.contentMarkdown}
        onChange={(e) => setForm((prev) => ({ ...prev, contentMarkdown: e.target.value }))}
        disabled={readOnly}
      />
      {error ? <div className="text-sm text-red-600">{error}</div> : null}
      <div className="flex gap-2">
        {!readOnly ? (
          <Button onClick={submit} disabled={saving}>
            {saving ? t('Saving...', 'Saving...') : t('Save Draft', 'Save Draft')}
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onCancel}>{t('Close', 'Close')}</Button>
      </div>
    </div>
  )
}

